import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import { ElementType } from '../../../../common/elements/elementTypes';
import { createSingleEditorTaskListContentWithItems } from '../../../../common/taskLists/singleEditorTaskListContentBuilder';
import { isTiptapEditorEmpty } from '../../../../common/tiptap/utils/isTiptapEditorEmpty';
import { DEFAULT_TIPTAP_EXTENSION_PRIORITY } from '../../../../common/tiptap/tiptapTypes';
import { readTaskListPasteContent } from '../../../../common/taskLists/taskListClipboardPasteUtils';
import { updateCurrentlyEditingElementType } from '../universalCardActions';

type CardTaskListPasteOptions = {
    dispatch: (action: unknown) => unknown;
    isSingleTaskListEditorEnabled: boolean;
};

/**
 * Turns an empty card into a task list when copied task items are pasted into it, rather than
 * parsing them into the card's own legacy checklist and losing their chip data.
 *
 * Only an empty card converts. Pasting into a card that already holds text is an ordinary paste -
 * the user is adding to a note, not creating a list.
 *
 * This is the same idea as the `UniversalCard` conversions (typing `[] ` makes a task list), but it
 * runs from a paste rather than a keystroke, so it needs the clipboard event that those commands
 * never see.
 */
export const CardTaskListPaste = Extension.create<CardTaskListPasteOptions>({
    name: 'cardTaskListPaste',

    // Ahead of any extension that would otherwise claim the paste and parse the HTML itself.
    priority: DEFAULT_TIPTAP_EXTENSION_PRIORITY + 10,

    addOptions() {
        return {
            ...this.parent?.(),
            dispatch: () => {},
            isSingleTaskListEditorEnabled: false,
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('cardTaskListPaste'),
                props: {
                    handlePaste: (view, event) => {
                        const { dispatch, isSingleTaskListEditorEnabled } = this.options;

                        if (!event.clipboardData) return false;
                        if (!isTiptapEditorEmpty(this.editor)) return false;

                        const pasteContent = readTaskListPasteContent({
                            clipboardData: event.clipboardData,
                            isSingleTaskListEditorEnabled,
                        });

                        if (!pasteContent) return false;

                        // The card is about to become a task list, so its own pending save must not
                        // land on top of the new content.
                        this.editor.commands.cancelPendingUpdate();

                        dispatch(
                            updateCurrentlyEditingElementType({
                                elementType: ElementType.TASK_LIST_TYPE,
                                data: createSingleEditorTaskListContentWithItems(
                                    pasteContent.taskItems,
                                    pasteContent.title,
                                ),
                            }),
                        );

                        return true;
                    },
                },
            }),
        ];
    },
});
